import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Chip,
  Stack,
  LinearProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  DirectionsRun as CardioIcon,
  Check as CheckIcon,
} from "@mui/icons-material";

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  transition: "box-shadow 0.3s ease",
  marginBottom: theme.spacing(3),
  overflow: "visible",
}));

const CardContentWrapper = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  "&:last-child": {
    paddingBottom: theme.spacing(3),
  },
}));

const GoalCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

const CardioGoalsCard = ({ report }) => {
  if (!report || !report.clientData || !report.clientData.cardioGoals)
    return null;

  const cardioGoals = report.clientData.cardioGoals.filter(
    (goal) => goal.active,
  );

  // Check if we have cardio session data in the health data
  const cardioSessions = [];
  if (report.healthData) {
    report.healthData.forEach((data) => {
      if (data.reportData && data.reportData.cardioGoals) {
        data.reportData.cardioGoals.forEach((cardio) => {
          cardioSessions.push({
            date: new Date(data.createdOn.seconds * 1000),
            cardioReport: cardio.cardioReport,
            createdOn: new Date(cardio.createdOn?.seconds * 1000) || new Date(),
          });
        });
      }
    });
  }

  // If no active cardio goals, don't show the card
  if (cardioGoals.length === 0 && cardioSessions.length === 0) {
    return null;
  }

  return (
    <StyledCard>
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <CardioIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold">
              Cardio Goals
            </Typography>
          </Stack>
        }
      />
      <CardContentWrapper>
        {cardioGoals.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No active cardio goals set for this client.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {cardioGoals.map((goal) => {
              // Count completed sessions for this goal
              const completedSessions = cardioSessions.filter(
                (session) => session.cardioReport.id === goal.id,
              ).length;

              // Calculate progress percentage
              const progressPercentage =
                goal.sessions > 0
                  ? Math.min(
                      Math.round((completedSessions / goal.sessions) * 100),
                      100,
                    )
                  : 0;

              return (
                <Grid item xs={12} md={6} lg={4} key={goal.id}>
                  <GoalCard>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {goal.name}
                      </Typography>

                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Chip
                          label={`${goal.duration}`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${goal.intensity} intensity`}
                          size="small"
                          variant="outlined"
                        />
                        {goal.targetHR && (
                          <Chip
                            label={`HR ${goal.targetHR}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Stack>

                      <Box sx={{ mt: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            Sessions completed
                          </Typography>
                          <Typography
                            component="span"
                            variant="body2"
                            fontWeight="medium"
                          >
                            {completedSessions} / {goal.sessions}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={progressPercentage}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: "grey.200",
                            "& .MuiLinearProgress-bar": {
                              backgroundColor: (theme) =>
                                theme.palette.success.main,
                            },
                          }}
                        />
                      </Box>
                    </Stack>
                  </GoalCard>
                </Grid>
              );
            })}
          </Grid>
        )}

        {cardioSessions.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
              Recent Cardio Activity
            </Typography>

            <Grid container spacing={1}>
              {cardioSessions.slice(0, 3).map((session, index) => (
                <Grid item xs={12} key={index}>
                  <Box
                    sx={{
                      p: 1.5,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderRadius: 1,
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2">
                        {session.cardioReport.name}
                      </Typography>
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        {session.date.toLocaleDateString()} •{" "}
                        {session.cardioReport.duration} •{" "}
                        {session.cardioReport.intensity} intensity
                      </Typography>
                    </Box>
                    <Chip
                      icon={<CheckIcon />}
                      label="Completed"
                      color="success"
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </CardContentWrapper>
    </StyledCard>
  );
};

export default CardioGoalsCard;
